import { z } from "zod";

import {
  cameraModeSchema,
  cameraOutputBaseSchema,
  cameraShotBaseSchema,
  cameraShotSchema,
  routeGeometrySchema,
} from "./cameraPlan";
import { POI_GROUP_IDS } from "./poiGroups";
import { profileDesignSchema, profileSourceSchema } from "./profileSamples";
import { languageSchema, unitLengthSchema, unitSlopeSchema } from "./searchParams";

/**
 * poiGroupIdSchema validates one POI group id against the canonical set in
 * poiGroups.ts. Zod-first because the value crosses the bus boundary from a
 * human toggle or an agent call into the store's grouped POI state; deriving the
 * enum from POI_GROUP_IDS keeps the accepted vocabulary in lockstep with the
 * group set, so a group added there is accepted here with no second edit.
 */
export const poiGroupIdSchema = z.enum(POI_GROUP_IDS);

/**
 * setPoiGroupInput validates a single-group toggle: the group to flip and
 * whether it should be enabled. Shared with the per-group POI tool so the bus
 * rejects an unknown group id before the handler reaches the store.
 */
export const setPoiGroupInput = z.object({
  enabled: z.boolean(),
  group: poiGroupIdSchema,
});

/**
 * SetPoiGroupInput is the validated single-group toggle payload, derived from
 * setPoiGroupInput.
 */
export type SetPoiGroupInput = z.infer<typeof setPoiGroupInput>;

/**
 * setPoiDisplayInput validates the all-groups POI display toggle: whether every
 * group should be enabled (true) or every group hidden (false). Shared with the
 * POI display tool — the show-all / hide-all switch the overlay menu's header
 * drives.
 */
export const setPoiDisplayInput = z.object({
  enabled: z.boolean(),
});

/**
 * SetPoiDisplayInput is the validated all-groups display toggle payload, derived
 * from setPoiDisplayInput.
 */
export type SetPoiDisplayInput = z.infer<typeof setPoiDisplayInput>;

/**
 * studioEmbedOpenInput validates the resource the embed wizard opens for: an
 * itinerary or a collection, exactly one. A row passes only the id; the wizard reads
 * everything else it needs (the org default props, the publishable key, the live
 * config) from server state and its own local form, so the open payload is just the
 * pointer. A union rather than two optional fields, so a caller cannot ask the wizard
 * to open for both resources or for none.
 *
 * Each branch is `.strict()` because that is what makes the union actually exclusive.
 * A plain Zod object ignores unknown keys, so `{ collectionId, itineraryId }` would
 * match the FIRST branch, have `itineraryId` stripped, and silently open the wizard
 * for the collection — the caller's second id discarded without a word. Strict turns
 * that into the parse failure the union is supposed to express.
 */
export const studioEmbedOpenInput = z.union([
  z.object({ collectionId: z.string() }).strict(),
  z.object({ itineraryId: z.string() }).strict(),
]);

/**
 * StudioEmbedOpenInput is the validated embed-wizard open payload, derived from
 * studioEmbedOpenInput.
 */
export type StudioEmbedOpenInput = z.infer<typeof studioEmbedOpenInput>;

/** studioEmbedCloseInput takes no fields — closing the wizard clears its slot. */
export const studioEmbedCloseInput = z.object({});

/**
 * studioShareOpenInput validates the itinerary the share-link modal opens for. An
 * itinerary row passes only the id; the modal mints (and rotates) the share token
 * through the REST layer and holds it in its own local state, so the open payload
 * is just the itinerary pointer.
 */
export const studioShareOpenInput = z.object({
  itineraryId: z.string(),
});

/**
 * StudioShareOpenInput is the validated share-link open payload, derived from
 * studioShareOpenInput.
 */
export type StudioShareOpenInput = z.infer<typeof studioShareOpenInput>;

/** studioShareCloseInput takes no fields — closing the modal clears its slot. */
export const studioShareCloseInput = z.object({});

/**
 * studioVideoOpenInput validates the itinerary the video export wizard opens for,
 * plus an optional segment to narrow the flight to one segment (absent/null = the
 * whole itinerary). An itinerary row passes only the id; a segment row passes the
 * segment id so the same wizard records a single segment.
 */
export const studioVideoOpenInput = z.object({
  itineraryId: z.string(),
  segmentId: z.string().nullable().optional(),
});

/**
 * StudioVideoOpenInput is the validated video-wizard open payload, derived from
 * studioVideoOpenInput.
 */
export type StudioVideoOpenInput = z.infer<typeof studioVideoOpenInput>;

/**
 * studioVideoRouteSetInput validates the flattened route the open wizard's Camera
 * Plan resolves against — null until the wizard's itinerary detail loads and the
 * route is computed — plus the optional plan-independent `profileSource` the live
 * profile overlay resolves per-frame samples from (null when the route is null,
 * absent when the caller computes no overlay source) and the optional
 * `activityGlyphUrl` — the resolved silhouette URL for the itinerary's activity that
 * the moving focus dot masks (null when the itinerary carries no activity, absent
 * when the caller resolves no glyph). The wizard fetches its exported itinerary by id
 * and computes them from its segments + activity, then dispatches them together, so
 * the route, the overlay source, AND the marker glyph are decoupled from the studio's
 * active itinerary. `profileSource` and `activityGlyphUrl` are optional so a route-only
 * dispatch still validates. Reusing routeGeometrySchema + profileSourceSchema keeps the
 * accepted shapes in lockstep with what the resolvers consume; the glyph is a plain URL
 * string so no app asset or ActivitySlug crosses into the map leaf.
 */
export const studioVideoRouteSetInput = z.object({
  activityGlyphUrl: z.string().nullable().optional(),
  profileSource: profileSourceSchema.nullable().optional(),
  route: routeGeometrySchema.nullable(),
});

/**
 * StudioVideoRouteSetInput is the validated route-set payload, derived from
 * studioVideoRouteSetInput.
 */
export type StudioVideoRouteSetInput = z.infer<typeof studioVideoRouteSetInput>;

/** studioVideoCloseInput takes no fields — closing the wizard clears its slot. */
export const studioVideoCloseInput = z.object({});

/**
 * studioVideoStepSetInput validates the wizard step to switch to: the format
 * picker, the profile-overlay step, the camera/preview step, or the review/record
 * step. It mirrors the VideoExport.step enum so the bus rejects an unknown step at
 * the boundary.
 */
export const studioVideoStepSetInput = z.object({
  step: z.enum(["format", "profile", "camera", "review"]),
});

/**
 * StudioVideoStepSetInput is the validated wizard-step payload, derived from
 * studioVideoStepSetInput.
 */
export type StudioVideoStepSetInput = z.infer<typeof studioVideoStepSetInput>;

/**
 * studioVideoModeSetInput validates the camera mode to author the plan in — a
 * preset (smart/cinematic/drone) regenerates the plan deterministically, while
 * `director` seeds a neutral empty plan an explicit shot sequence fills. Reusing
 * cameraModeSchema keeps the accepted vocabulary in lockstep with the plan.
 */
export const studioVideoModeSetInput = z.object({
  mode: cameraModeSchema,
});

/**
 * StudioVideoModeSetInput is the validated mode-set payload, derived from
 * studioVideoModeSetInput.
 */
export type StudioVideoModeSetInput = z.infer<typeof studioVideoModeSetInput>;

/**
 * studioVideoDurationSetInput validates the whole flight's target wall-clock
 * length in milliseconds (20..120 s, matching cameraPlanSchema's targetDurationMs
 * bounds) so an out-of-range duration is rejected at the boundary — a value below
 * the floor no UI path emits but an agent dispatch could.
 */
export const studioVideoDurationSetInput = z.object({
  targetDurationMs: z.number().min(20_000).max(120_000),
});

/**
 * StudioVideoDurationSetInput is the validated duration-set payload, derived from
 * studioVideoDurationSetInput.
 */
export type StudioVideoDurationSetInput = z.infer<typeof studioVideoDurationSetInput>;

/**
 * studioVideoOutputSetInput validates a partial encoder-output reconfiguration:
 * any subset of the output fields (format, resolution, fps, aspect, bitrate). It
 * is cameraOutputBaseSchema (the no-default field set) made partial so a caller
 * updates one field (the aspect, say) without restating the rest, and an absent
 * field stays absent rather than parsing to a default. The handler merges only the
 * present fields over the plan's current output, so a single-field update never
 * resets its siblings. At least one field is required so an empty update is
 * rejected. The base must carry no field-level defaults: a `.default()` would
 * survive `.partial()` and pre-fill every key, making this refinement inert and
 * the merge a full-output reset.
 */
export const studioVideoOutputSetInput = cameraOutputBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "output.set requires at least one field",
  });

/**
 * StudioVideoOutputSetInput is the validated partial output payload, derived from
 * studioVideoOutputSetInput.
 */
export type StudioVideoOutputSetInput = z.infer<typeof studioVideoOutputSetInput>;

/**
 * studioVideoPresetSetInput validates the ready-made framing preset to apply —
 * `vertical`, `widescreen`, or `square`, the three platform-true templates (the
 * `custom` member of the preset enum is not a template, so it is excluded here: the
 * Custom path is the org-gated output.set, not a preset stamp). The handler spreads
 * the chosen VIDEO_PRESETS template over the plan's output and stamps the preset
 * name.
 */
export const studioVideoPresetSetInput = z.object({
  preset: z.enum(["vertical", "widescreen", "square"]),
});

/**
 * StudioVideoPresetSetInput is the validated preset-set payload, derived from
 * studioVideoPresetSetInput.
 */
export type StudioVideoPresetSetInput = z.infer<typeof studioVideoPresetSetInput>;

/**
 * studioVideoProfileToggleInput validates the live profile overlay enable flag —
 * whether the Tour-de-France-style data overlay is composited into the preview and
 * the recording. Shared with the profile-toggle tool so the bus drives the same
 * flip for a human click and an agent call.
 */
export const studioVideoProfileToggleInput = z.object({
  enabled: z.boolean(),
});

/**
 * StudioVideoProfileToggleInput is the validated profile-toggle payload, derived
 * from studioVideoProfileToggleInput.
 */
export type StudioVideoProfileToggleInput = z.infer<typeof studioVideoProfileToggleInput>;

/**
 * studioVideoProfileDesignSetInput validates the live profile overlay design to
 * switch to — `graph` (elevation curve) or `strip` (compact band). Reusing
 * profileDesignSchema keeps the accepted vocabulary in lockstep with the overlay
 * renderer registry.
 */
export const studioVideoProfileDesignSetInput = z.object({
  design: profileDesignSchema,
});

/**
 * StudioVideoProfileDesignSetInput is the validated profile-design payload, derived
 * from studioVideoProfileDesignSetInput.
 */
export type StudioVideoProfileDesignSetInput = z.infer<typeof studioVideoProfileDesignSetInput>;

/**
 * studioVideoProfileUnitsSetInput validates the video-scoped units the profile
 * overlay formats with: the `length` unit (metric/imperial) and the `slope` unit
 * (degrees/percentage). These are local to the export — they never touch the global
 * preferences or the map — so the overlay reads them straight into the param-based
 * formatters. Reusing the domain unit schemas keeps the values as the shared domain
 * types, never free strings.
 */
export const studioVideoProfileUnitsSetInput = z.object({
  length: unitLengthSchema,
  slope: unitSlopeSchema,
});

/**
 * StudioVideoProfileUnitsSetInput is the validated profile-units payload, derived
 * from studioVideoProfileUnitsSetInput.
 */
export type StudioVideoProfileUnitsSetInput = z.infer<typeof studioVideoProfileUnitsSetInput>;

/**
 * studioVideoProfileLanguageSetInput validates the video-scoped language the
 * profile overlay localizes its labels in. Like the units, it is local to the
 * export and reused as the domain Language type via languageSchema rather than a
 * free string.
 */
export const studioVideoProfileLanguageSetInput = z.object({
  language: languageSchema,
});

/**
 * StudioVideoProfileLanguageSetInput is the validated profile-language payload,
 * derived from studioVideoProfileLanguageSetInput.
 */
export type StudioVideoProfileLanguageSetInput = z.infer<typeof studioVideoProfileLanguageSetInput>;

/**
 * studioVideoShotAddInput validates a new keyframe added to the plan: every
 * cameraShot field except `id` (the tool mints a stable id). The schema's bounds
 * and defaults apply, so a sparse `{ at, pitchDeg, zoom }` parses with `follow`
 * bearing, no hold, and unit speed.
 */
export const studioVideoShotAddInput = cameraShotSchema.omit({ id: true });

/**
 * StudioVideoShotAddInput is the validated shot-add payload, derived from
 * studioVideoShotAddInput.
 */
export type StudioVideoShotAddInput = z.infer<typeof studioVideoShotAddInput>;

/**
 * studioVideoShotSetInput validates an edit to one existing keyframe: the `id` of
 * the shot to change plus any subset of its editable fields. The fields are the
 * cameraShotBaseSchema fields (the no-default set, sans id) made optional, so a
 * caller nudges one property (the pitch, the hold) without restating the shot, and
 * an absent field stays absent rather than parsing to a default. The handler
 * merges only the present fields over the matching shot and re-sorts if `at`
 * moved, so a single-field edit never resets its siblings. At least one editable
 * field is required. The base must carry no field-level defaults: a `.default()`
 * would survive `.partial()` and pre-fill the non-id keys, making this refinement
 * inert and an id-only edit a silent reset of bearingMode, holdMs, and speedScale.
 */
export const studioVideoShotSetInput = cameraShotBaseSchema
  .omit({ id: true })
  .partial()
  .extend({ id: z.string() })
  .refine((value) => Object.keys(value).some((key) => key !== "id"), {
    message: "shot.set requires at least one field to change",
  });

/**
 * StudioVideoShotSetInput is the validated shot-edit payload, derived from
 * studioVideoShotSetInput.
 */
export type StudioVideoShotSetInput = z.infer<typeof studioVideoShotSetInput>;

/** studioVideoShotRemoveInput validates the id of the keyframe to remove. */
export const studioVideoShotRemoveInput = z.object({
  id: z.string(),
});

/**
 * StudioVideoShotRemoveInput is the validated shot-remove payload, derived from
 * studioVideoShotRemoveInput.
 */
export type StudioVideoShotRemoveInput = z.infer<typeof studioVideoShotRemoveInput>;

/**
 * studioVideoShotAddHoldInput validates a hold beat added to the plan: a keyframe
 * at progress `at` that dwells for `holdMs` while framing the route with the given
 * `pitchDeg`/`zoom`. It is the director's "pause here" verb, distinct from a
 * travelling shot — the camera parks at the point for the dwell. The bounds match
 * cameraShotSchema's so an out-of-range hold or framing is rejected.
 */
export const studioVideoShotAddHoldInput = z.object({
  at: z.number().min(0).max(1),
  holdMs: z.number().min(0).max(10_000),
  pitchDeg: z.number().min(0).max(85),
  zoom: z.number().min(0).max(22),
});

/**
 * StudioVideoShotAddHoldInput is the validated hold-beat payload, derived from
 * studioVideoShotAddHoldInput.
 */
export type StudioVideoShotAddHoldInput = z.infer<typeof studioVideoShotAddHoldInput>;

/**
 * studioVideoOrbitInput validates an orbit around the point at progress `at`: the
 * camera parks there and sweeps its heading through `turns` full rotations (1..4,
 * default 1) over a short progress window, framing with the given `pitchDeg` and
 * `zoom`, `clockwise` choosing the direction (default true), and `dwellMs` the
 * total time the sweep spans (the orbit's hold budget). It is the director's
 * "circle this spot" verb — the tool expands it into a run of explicit
 * fixed-bearing keyframes the resolver sweeps between.
 */
export const studioVideoOrbitInput = z.object({
  at: z.number().min(0).max(1),
  clockwise: z.boolean().default(true),
  dwellMs: z.number().min(0).max(10_000).default(4_000),
  pitchDeg: z.number().min(0).max(85),
  turns: z.number().int().min(1).max(4).default(1),
  zoom: z.number().min(0).max(22),
});

/**
 * StudioVideoOrbitInput is the validated orbit payload, derived from
 * studioVideoOrbitInput.
 */
export type StudioVideoOrbitInput = z.infer<typeof studioVideoOrbitInput>;

/** studioVideoPlanGetInput takes no fields — it reads the current plan. */
export const studioVideoPlanGetInput = z.object({});

/** studioVideoPreviewSeekInput validates the progress fraction (0..1) to seek the preview to. */
export const studioVideoPreviewSeekInput = z.object({
  fraction: z.number().min(0).max(1),
});

/**
 * StudioVideoPreviewSeekInput is the validated preview-seek payload, derived from
 * studioVideoPreviewSeekInput.
 */
export type StudioVideoPreviewSeekInput = z.infer<typeof studioVideoPreviewSeekInput>;

/**
 * studioVideoControlInput is the empty input shared by the parameterless
 * preview/record control tools (preview start/play/pause/stop, record, cancel,
 * download) — they act on the open wizard's plan and the run singleton, carrying
 * no payload of their own.
 */
export const studioVideoControlInput = z.object({});
